import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionProps, AccordionSummary, Box, Stack, styled, useTheme } from "@mui/material";
import { MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { MprcFeedTypography } from 'components/MprcFeedTypography';
import { FeedType } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { NIL } from "uuid";
import { AttachsByRefId } from 'views/knowledge/components/card/behaviors/common/DefaultAttach';
import { StickerBox } from "views/knowledge/components/card/behaviors/common/StickerBox";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { CardContextLink } from 'views/knowledge/components/card/CardContextLink';

interface ISection {
    id: string;
    name: string;
    content: string;
}


const Accordion2 = styled((props: AccordionProps) => (
    <Accordion {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
}));

///
export const BinderTitle = () => {
    const { pub, otherInfo, pub: { expired, availableInTheFuture } } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");

    const isBlocked = pub.blocked?.blockedOn != undefined;

    const color = isBlocked
        ? theme.palette.warning.main
        : expired || availableInTheFuture
            ? theme.palette.muted.light
            : theme.palette.common.black;

    var borderStyle: React.CSSProperties | undefined = otherInfo?.feedType != FeedType.ContentPage && (pub.interaction.isNewPublication || pub.blocked?.firstAccessAfterBlock === true || pub.rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    const navigateUrl = KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.masterPublication?.publicationId);

    return (
        <div style={borderStyle}>
            {
                navigateUrl == document.location.pathname
                    ? (
                        <Box sx={{ color: color }}>
                            <MprcFeedTypography variant="largeTitle" component="div" noWrap={true} color={"inherit"}>
                                <span title={pub.title}>{pub.title}</span>
                            </MprcFeedTypography>
                        </Box>
                    )
                    : (
                        <CardContextLink
                            publicationId={pub.publicationId}
                            to={navigateUrl}
                        >
                            <Box sx={{ color: color }}>
                                <MprcFeedTypography variant="largeTitle" component="div" noWrap={true} color={"inherit"}>
                                    <span title={pub.title}>{pub.title}</span>
                                </MprcFeedTypography>
                            </Box>
                        </CardContextLink>
                    )
            }
            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
                {expired && <StickerBox name={t("Feed.Card.Common.ExpiredText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {availableInTheFuture && <StickerBox name={t("Feed.Card.Common.DisplayOnText", { date: Formaters.dayMonth(pub.viewPeriodStart) })} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {pub.sticker && <StickerBox name={pub.sticker.name} textColor={pub.sticker.textColor} backgroundColor={pub.sticker.backgroundColor} />}
            </Stack>
        </div>
    );
};


///
export const BinderPageContent = () => {
    const {
        pub: { publicationId },
    } = useCardContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(undefined);
    const api = useCoreApi();
    const [pages, setPages] = useState<ISection[]>([]);
    const [expanded, setExpanded] = useState<string | false>(false);

    //
    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    ///
    const readPages = (htmlString: string): ISection[] => {
        const parser = new DOMParser();
        const doc: Document = parser.parseFromString(htmlString, 'text/html');
        const sections = doc.querySelectorAll("section");

        const pages: ISection[] = [];

        for (var i = 0; i < sections.length; i++) {

            const id = sections[i].getAttribute("id") ?? NIL;
            const name = sections[i].getAttribute("name") ?? `Ficha ${i + 1}`;
            pages.push({ id, name, content: sections[i].getHTML() });
        }

        return pages;
    };

    const loadContent = () => {
        setLoading(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                const pages = readPages(result.data);
                setPages(pages);
                setExpanded(pages[0].id);
            })
            .catch((e) => setError(e))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadContent();
    }, []);

    return error
        ? <MprcRequestErrorReport error={error} />
        : (
            <Box sx={{ mt: 1, mb: 1 }}>
                {
                    loading
                        ? <MprcSpinnerIndicator />
                        : pages.map((page, index) => {
                            return (
                                <Accordion2 key={page.id} expanded={expanded === page.id} onChange={handleChange(page.id)}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} id={page.id} >
                                        <MprcFeedTypography variant="title">{page.name}</MprcFeedTypography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <section id="k-content">
                                            <div dangerouslySetInnerHTML={{ __html: page.content }} className="ql-snow ql-editor"></div>
                                        </section>
                                        <AttachsByRefId pageRefId={page.id} />
                                    </AccordionDetails>
                                </Accordion2>
                            );
                        })
                }
            </Box>
        );
};
